import { ProjectScript, ProjectScriptUpdateCommand } from "./projectScriptCommands";
export { ProjectScript, ProjectScriptIcon } from "./projectScriptCommands";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as SchemaIssue from "effect/SchemaIssue";
import * as Struct from "effect/Struct";
import { ClaudeModelOptions, CodexModelOptions, ProviderOptionSelection } from "./model";
import { RepositoryIdentity } from "./environment";
import { ThreadEnvMode } from "./baseSchemas";
import { SourceControlProviderKind } from "./sourceControl";
import {
  ApprovalRequestId,
  CheckpointRef,
  CommandId,
  EventId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  ProviderItemId,
  ThreadId,
  TrimmedNonEmptyString,
  TrimmedString,
  TurnId,
} from "./baseSchemas";

export const ORCHESTRATION_WS_METHODS = {
  dispatchCommand: "orchestration.dispatchCommand",
  getTurnDiff: "orchestration.getTurnDiff",
  getFullThreadDiff: "orchestration.getFullThreadDiff",
  streamTurnDiff: "orchestration.streamTurnDiff",
  streamFullThreadDiff: "orchestration.streamFullThreadDiff",
  replayEvents: "orchestration.replayEvents",
  getArchivedShellSnapshot: "orchestration.getArchivedShellSnapshot",
  subscribeShell: "orchestration.subscribeShell",
  subscribeThread: "orchestration.subscribeThread",
  searchThreads: "orchestration.searchThreads",
  getThreadDetailPage: "orchestration.getThreadDetailPage",
} as const;

export const ProviderKind = Schema.Literals([
  "codex",
  "claudeAgent",
  "ollama",
  "cmmd",
  "apex",
  "cursor",
  "opencode",
]);
export type ProviderKind = typeof ProviderKind.Type;
export const ProviderApprovalPolicy = Schema.Literals([
  "untrusted",
  "on-failure",
  "on-request",
  "never",
]);
export type ProviderApprovalPolicy = typeof ProviderApprovalPolicy.Type;
export const ProviderSandboxMode = Schema.Literals([
  "read-only",
  "workspace-write",
  "danger-full-access",
]);
export type ProviderSandboxMode = typeof ProviderSandboxMode.Type;

export const DEFAULT_PROVIDER_KIND: ProviderKind = "codex";

export const ProviderAccountId = TrimmedNonEmptyString;
export type ProviderAccountId = typeof ProviderAccountId.Type;

export const CodexModelSelection = Schema.Struct({
  provider: Schema.Literal("codex"),
  model: TrimmedNonEmptyString,
  providerAccountId: Schema.optionalKey(ProviderAccountId),
  options: Schema.optionalKey(CodexModelOptions),
});
export type CodexModelSelection = typeof CodexModelSelection.Type;

export const ClaudeModelSelection = Schema.Struct({
  provider: Schema.Literal("claudeAgent"),
  model: TrimmedNonEmptyString,
  providerAccountId: Schema.optionalKey(ProviderAccountId),
  options: Schema.optionalKey(ClaudeModelOptions),
});
export type ClaudeModelSelection = typeof ClaudeModelSelection.Type;

export const OllamaModelSelection = Schema.Struct({
  provider: Schema.Literal("ollama"),
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(Schema.Undefined),
});
export type OllamaModelSelection = typeof OllamaModelSelection.Type;

export const CmmdModelSelection = Schema.Struct({
  provider: Schema.Literal("cmmd"),
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(Schema.Undefined),
});
export type CmmdModelSelection = typeof CmmdModelSelection.Type;

export const ApexModelSelection = Schema.Struct({
  provider: Schema.Literal("apex"),
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(Schema.Undefined),
});
export type ApexModelSelection = typeof ApexModelSelection.Type;

// Cursor + OpenCode are ACP-based runtimes. Their per-turn options
// (reasoning effort, context window, thinking toggle, fast-mode) are
// carried as a flat ProviderOptionSelection array so the adapter can pass
// them into ACP `session/set_config_option` without baking them into
// provider-specific option structs. The list of supported option ids is
// discovered at session-start from the agent's reported config options.
export const CursorModelSelection = Schema.Struct({
  provider: Schema.Literal("cursor"),
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(Schema.Array(ProviderOptionSelection)),
});
export type CursorModelSelection = typeof CursorModelSelection.Type;

export const OpenCodeModelSelection = Schema.Struct({
  provider: Schema.Literal("opencode"),
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(Schema.Array(ProviderOptionSelection)),
});
export type OpenCodeModelSelection = typeof OpenCodeModelSelection.Type;

export const ModelSelection = Schema.Union([
  CodexModelSelection,
  ClaudeModelSelection,
  OllamaModelSelection,
  CmmdModelSelection,
  ApexModelSelection,
  CursorModelSelection,
  OpenCodeModelSelection,
]);
export type ModelSelection = typeof ModelSelection.Type;

export const RuntimeMode = Schema.Literals([
  "approval-required",
  "auto-accept-edits",
  "full-access",
]);
export type RuntimeMode = typeof RuntimeMode.Type;
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";
/**
 * Which wallet pays for a CMMD Work Session's provider usage. The values
 * match CMMD's `forgeWorkSessionWallets` in `shared/schema/forge-work-sessions.ts`.
 *
 * CMMD stamps it on the kick `thread.turn.start` (CMMD#5917). An absent value
 * means the turn carries no wallet claim, which Forge treats the way it
 * treated every turn before the field existed: on the personal login. On a
 * CMMD cloud runtime a `workspace` Claude turn must run through CMMD's metered
 * egress and is refused if CMMD will not mint for it. It never falls back to
 * the personal login in the VM.
 */
export const WorkSessionWallet = Schema.Literals(["personal", "workspace", "byok"]);
export type WorkSessionWallet = typeof WorkSessionWallet.Type;
export const ProviderInteractionMode = Schema.Literals(["default", "plan"]);
export type ProviderInteractionMode = typeof ProviderInteractionMode.Type;
export const DEFAULT_PROVIDER_INTERACTION_MODE: ProviderInteractionMode = "default";
export const ProviderRequestKind = Schema.Literals(["command", "file-read", "file-change"]);
export type ProviderRequestKind = typeof ProviderRequestKind.Type;
export const AssistantDeliveryMode = Schema.Literals(["buffered", "streaming"]);
export type AssistantDeliveryMode = typeof AssistantDeliveryMode.Type;
export const ProviderApprovalDecision = Schema.Literals([
  "accept",
  "acceptForSession",
  "decline",
  "cancel",
]);
export type ProviderApprovalDecision = typeof ProviderApprovalDecision.Type;
export const ProviderUserInputAnswers = Schema.Record(Schema.String, Schema.Unknown);
export type ProviderUserInputAnswers = typeof ProviderUserInputAnswers.Type;

export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 8;
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const PROVIDER_SEND_TURN_MAX_FILE_BYTES = 50 * 1024 * 1024;
const PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS = 14_000_000;
const CHAT_ATTACHMENT_ID_MAX_CHARS = 128;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
export type CorrelationId = typeof CorrelationId.Type;

const ChatAttachmentId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS),
  Schema.isPattern(/^[a-z0-9_-]+$/i),
);
export type ChatAttachmentId = typeof ChatAttachmentId.Type;

export const ChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
});
export type ChatImageAttachment = typeof ChatImageAttachment.Type;

export const ChatFileAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100)),
  sizeBytes: NonNegativeInt.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_FILE_BYTES),
  ),
});
export type ChatFileAttachment = typeof ChatFileAttachment.Type;

const UploadChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
  dataUrl: TrimmedNonEmptyString.check(
    Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS),
  ),
});
export type UploadChatImageAttachment = typeof UploadChatImageAttachment.Type;

export const ChatAttachment = Schema.Union([ChatImageAttachment, ChatFileAttachment]);
export type ChatAttachment = typeof ChatAttachment.Type;
export const UserInputAttachments = Schema.Record(
  Schema.String,
  Schema.Array(ChatAttachment).check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS)),
);
export type UserInputAttachments = typeof UserInputAttachments.Type;
const UploadChatAttachment = Schema.Union([UploadChatImageAttachment]);
export type UploadChatAttachment = typeof UploadChatAttachment.Type;

/**
 * Availability of the project's `workspaceRoot` on the server's filesystem.
 *
 * Resolved on-demand alongside `repositoryIdentity` when the snapshot is
 * built. Old worktrees frequently get deleted on disk while their project
 * row sticks around in the projection — those rows show up as `missing`
 * so the sidebar can dim them and offer a one-click archive instead of
 * letting them masquerade as live projects.
 *
 * `unknown` is the conservative fallback when the probe itself errored
 * (permission denied, IO timeout, etc.) — render unchanged so we never
 * accidentally hide a healthy project.
 */
export const ProjectAvailability = Schema.Literals(["available", "missing", "unknown"]);
export type ProjectAvailability = typeof ProjectAvailability.Type;

/**
 * The CMMD record that places one Forge Project onto one Environment.
 *
 * The organization and the CMMD project together are the durable identity of a
 * Checkout on that Environment, and the key every lookup uses; a directory path
 * only breaks ties. A Forge Cloud Environment is per (organization, user), so
 * every Checkout on it reports the same workspace root and, once connected,
 * an empty `cwd` — nothing about a directory can tell them apart.
 *
 * Optional everywhere it appears: a project created before this field existed,
 * or one added by hand rather than from a Registration, carries none and is
 * still matched by the directory rules.
 */
export const CmmdRegistration = Schema.Struct({
  cmmdOrganizationId: PositiveInt,
  cmmdProjectId: PositiveInt,
});
export type CmmdRegistration = typeof CmmdRegistration.Type;

export const OrchestrationProject = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  availability: Schema.optional(ProjectAvailability),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  /**
   * Where this project's new threads start; absent/null means "use the app
   * setting". Optional rather than required-nullable because this is replayed
   * history and a wire contract -- every project created before this field
   * existed must still decode.
   */
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  cmmdRegistration: Schema.optional(Schema.NullOr(CmmdRegistration)),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationProject = typeof OrchestrationProject.Type;

export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
export type OrchestrationMessageRole = typeof OrchestrationMessageRole.Type;

export const OrchestrationMessage = Schema.Struct({
  id: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationMessage = typeof OrchestrationMessage.Type;

export const OrchestrationProposedPlanId = TrimmedNonEmptyString;
export type OrchestrationProposedPlanId = typeof OrchestrationProposedPlanId.Type;

export const OrchestrationProposedPlan = Schema.Struct({
  id: OrchestrationProposedPlanId,
  turnId: Schema.NullOr(TurnId),
  planMarkdown: TrimmedNonEmptyString,
  implementedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  implementationThreadId: Schema.NullOr(ThreadId).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProposedPlan = typeof OrchestrationProposedPlan.Type;

const SourceProposedPlanReference = Schema.Struct({
  threadId: ThreadId,
  planId: OrchestrationProposedPlanId,
});

export const OrchestrationSessionStatus = Schema.Literals([
  "idle",
  "starting",
  "running",
  "ready",
  "interrupted",
  "stopped",
  "error",
]);
export type OrchestrationSessionStatus = typeof OrchestrationSessionStatus.Type;

export const OrchestrationSession = Schema.Struct({
  threadId: ThreadId,
  status: OrchestrationSessionStatus,
  providerName: Schema.NullOr(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  activeTurnId: Schema.NullOr(TurnId),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});
export type OrchestrationSession = typeof OrchestrationSession.Type;

export const OrchestrationCheckpointFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: TrimmedNonEmptyString,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type OrchestrationCheckpointFile = typeof OrchestrationCheckpointFile.Type;

export const OrchestrationCheckpointStatus = Schema.Literals(["ready", "missing", "error"]);
export type OrchestrationCheckpointStatus = typeof OrchestrationCheckpointStatus.Type;

export const OrchestrationCheckpointSummary = Schema.Struct({
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type OrchestrationCheckpointSummary = typeof OrchestrationCheckpointSummary.Type;

export const OrchestrationThreadActivityTone = Schema.Literals([
  "info",
  "tool",
  "approval",
  "error",
]);
export type OrchestrationThreadActivityTone = typeof OrchestrationThreadActivityTone.Type;

export const OrchestrationThreadActivity = Schema.Struct({
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  payload: Schema.Unknown,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});
export type OrchestrationThreadActivity = typeof OrchestrationThreadActivity.Type;

const OrchestrationLatestTurnState = Schema.Literals([
  "running",
  "interrupted",
  "completed",
  "error",
]);
export type OrchestrationLatestTurnState = typeof OrchestrationLatestTurnState.Type;

export const OrchestrationLatestTurn = Schema.Struct({
  turnId: TurnId,
  state: OrchestrationLatestTurnState,
  requestedAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
  assistantMessageId: Schema.NullOr(MessageId),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
});
export type OrchestrationLatestTurn = typeof OrchestrationLatestTurn.Type;

/** The kind of CMMD entity a thread turn is anchored to. */
export const CmmdAnchorKind = Schema.Literals(["campaign", "project", "task", "scratch"]);
export type CmmdAnchorKind = typeof CmmdAnchorKind.Type;

/**
 * Thread kind — distinguishes master threads (top-level agent threads that may
 * spawn workers) from worker threads (subordinate threads that live in a
 * separate git worktree + branch and merge back into their master).
 *
 * Defaults to 'master' on existing thread records that pre-date M3.
 */
export const ThreadKind = Schema.Literals(["master", "worker"]);
export type ThreadKind = typeof ThreadKind.Type;

/**
 * Worker role — coarse vocabulary describing a worker thread's intent.
 *
 * Drives the role badge + color mapping in the WorkBar agents row. Fixed enum
 * so UI color tokens are stable and i18n-friendly. Null-tolerant throughout:
 * legacy rows and workers spawned before a role is assigned decode to
 * `null`, and the client falls back to a neutral "Worker" badge.
 */
export const WorkerRole = Schema.Literals(["swe", "qa", "reviewer", "designer", "pm", "architect"]);
export type WorkerRole = typeof WorkerRole.Type;

/**
 * Worker/master thread lifecycle status. Distinct from {@link OrchestrationSessionStatus}
 * which tracks the provider session state — this tracks the orchestration-level
 * status the sidebar + merge orchestrator care about.
 *
 * Defaults to 'idle' on existing thread records that pre-date M3.
 */
export const ThreadStatus = Schema.Literals([
  "idle",
  "running",
  "stuck",
  "needs-review",
  "ready",
  "merged",
  "conflicted",
  "archived",
]);
export type ThreadStatus = typeof ThreadStatus.Type;

/** User-visible lifecycle override, distinct from orchestration execution status. */
export const SettledOverride = Schema.NullOr(Schema.Literals(["settled", "active"]));
export type SettledOverride = typeof SettledOverride.Type;

// ── Auto-create CMMD task prompts (surfaced to master UI, M4.5) ────────────────
//
// When a master proposes a plan with sub-items and `autoCreateTasks === 'ask'`,
// the server emits this prompt payload. The master UI (M4.5) renders a card.
// With `autoCreateTasks === 'always'` the server creates tasks without prompting.

export const AutoCreateTaskPromptItem = Schema.Struct({
  slug: TrimmedNonEmptyString.check(
    Schema.isMaxLength(64),
    Schema.isPattern(/^[a-z0-9][a-z0-9_-]*$/),
  ),
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(500)),
  summary: TrimmedNonEmptyString.check(Schema.isMaxLength(10_000)),
});
export type AutoCreateTaskPromptItem = typeof AutoCreateTaskPromptItem.Type;

const AutoCreateTaskPromptId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(200),
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/),
);

export const AutoCreateTaskPrompt = Schema.Struct({
  promptId: AutoCreateTaskPromptId,
  masterThreadId: ThreadId,
  anchorKind: CmmdAnchorKind,
  anchorCmmdId: Schema.NullOr(Schema.String),
  items: Schema.Array(AutoCreateTaskPromptItem).check(Schema.isMinLength(1), Schema.isMaxLength(5)),
  state: Schema.optional(Schema.Literals(["pending_approval", "materializing"])),
  createdAt: IsoDateTime,
});
export type AutoCreateTaskPrompt = typeof AutoCreateTaskPrompt.Type;

/**
 * An in-flight title regeneration. Present only while a request is running;
 * `requestId` is the commandId of the `thread.meta.update` that started it, so
 * a completion arriving for a superseded request can be recognised and dropped
 * rather than clobbering a newer title.
 */
export const ThreadTitleRegeneration = Schema.Struct({
  requestId: CommandId,
  startedAt: IsoDateTime,
});
export type ThreadTitleRegeneration = typeof ThreadTitleRegeneration.Type;

const ThreadRateLimitWindowStatus = Schema.Struct({
  status: Schema.Literals(["allowed", "allowed_warning", "rejected"]),
  usedPercent: Schema.Number,
  resetsAt: IsoDateTime,
});

/**
 * Last-known 5h/7d telemetry for one provider account this thread has used.
 *
 * Keyed by ACCOUNT as well as provider. Keying by provider alone meant that
 * after switching from account A to account B the banner immediately
 * re-attributed A's rejection to B, kept offering A as the account to switch
 * to, and flip-flopped as fresh telemetry arrived. `null` is a report from
 * before accounts were recorded, or from a provider with no account binding.
 */
export const ThreadProviderRateLimitStatus = Schema.Struct({
  provider: Schema.Literals(["claude", "codex"]),
  providerAccountId: Schema.optional(Schema.NullOr(ProviderAccountId)).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  fiveHour: Schema.NullOr(ThreadRateLimitWindowStatus),
  sevenDay: Schema.NullOr(ThreadRateLimitWindowStatus),
});
export type ThreadProviderRateLimitStatus = typeof ThreadProviderRateLimitStatus.Type;

/**
 * Durable identity for the pull request a thread is working on.
 *
 * This deliberately excludes provider-owned state such as title, checks,
 * review state, or mergeability. Command remains the authority for those
 * volatile values; Forge only stores the identity needed to re-open it.
 */
export const LinkedPullRequest = Schema.Struct({
  projectId: ProjectId,
  /** Command-owned repository link; required to reopen the canonical PR detail surface. */
  projectRepositoryId: PositiveInt,
  provider: SourceControlProviderKind,
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  url: TrimmedNonEmptyString,
}).check(
  Schema.makeFilter(
    (input) => {
      try {
        const url = new URL(input.url);
        return (
          (url.protocol === "https:" || url.protocol === "http:") &&
          url.host.toLowerCase() === input.host.toLowerCase()
        );
      } catch {
        return false;
      }
    },
    { identifier: "CanonicalLinkedPullRequestUrl" },
  ),
);
export type LinkedPullRequest = typeof LinkedPullRequest.Type;

/**
 * The provider and model one turn actually ran with.
 *
 * `OrchestrationThread.modelSelection` is the LIVE picker value, so it changes
 * under turns that already finished. Usage rows are keyed on the model in
 * CMMD's ledger, so a turn republished under a later selection lands as a
 * second row for spend that happened once. The projection derives this from
 * the turn's own `thread.turn-start-requested` model, or the thread selection
 * in force at that event, and it never moves again.
 */
export const OrchestrationTurnModelSelection = Schema.Struct({
  turnId: TurnId,
  modelSelection: ModelSelection,
});
export type OrchestrationTurnModelSelection = typeof OrchestrationTurnModelSelection.Type;

export const OrchestrationThread = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /** Durable quiet-state timestamp. Archiving is a later storage-release state. */
  settledAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  /** Explicit user override; null means the automatic settlement ladder applies. */
  settledOverride: Schema.optional(SettledOverride),
  deletedAt: Schema.NullOr(IsoDateTime),
  messages: Schema.Array(OrchestrationMessage),
  proposedPlans: Schema.Array(OrchestrationProposedPlan).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  activities: Schema.Array(OrchestrationThreadActivity),
  /**
   * One entry per turn the projection holds a model for. Absent on records
   * written before this existed and on replicas, which carry their usage rows
   * verbatim instead of rebuilding them.
   */
  turnModelSelections: Schema.optional(Schema.Array(OrchestrationTurnModelSelection)),
  checkpoints: Schema.Array(OrchestrationCheckpointSummary),
  session: Schema.NullOr(OrchestrationSession),
  anchorKind: CmmdAnchorKind.pipe(
    Schema.withDecodingDefault(Effect.succeed<CmmdAnchorKind>("scratch")),
  ),
  anchorCmmdId: Schema.NullOr(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  linkedPullRequest: Schema.optionalKey(Schema.NullOr(LinkedPullRequest)),
  /** Thread kind — defaults to 'master' for back-compat with pre-M3 records. */
  kind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed<ThreadKind>("master"))),
  /** For worker threads, the master that spawned them. Null for master threads. */
  parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /** Orchestration status — defaults to 'idle' for back-compat with pre-M3 records. */
  status: ThreadStatus.pipe(Schema.withDecodingDefault(Effect.succeed<ThreadStatus>("idle"))),
  /**
   * Worker role — only meaningful when `kind === "worker"`. Null for masters
   * and for workers where a role was never assigned. Decodes to null on
   * pre-WorkBar rows.
   */
  workerRole: Schema.NullOr(WorkerRole).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /**
   * The CMMD Work Session id this thread's root turn was kicked for, when
   * CMMD started it from a card rather than a person opening the app. Null
   * for every other thread. A runtime with no owner JWT reads this to know
   * whether a Sidekick capability broker request has anything to bind to
   * (forge#3466). Absent on rows and events that pre-date this field.
   */
  workSessionId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /**
   * ISO timestamp of the last accepted Work Session binding on this thread.
   * Set to `command.createdAt` on thread.create when a workSessionId is
   * present, on a workSessionId rebind, and on a same-session re-kick that
   * strictly advances this timestamp (forge#3597). Used by the decider to
   * reject a late kick whose `createdAt` is not strictly after this value
   * (last kick wins). Absent on pre-forge#3597 rows.
   */
  workSessionBoundAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /**
   * The wallet the last CMMD kick named for this thread, set from
   * `thread.create` and moved by a `thread.turn.start` that names a different
   * one (CMMD#5917). A turn without a wallet, such as a person typing into
   * the thread, starts its provider session on this one, and because it lives
   * on the thread row it survives a server restart. Null when no kick ever
   * named one. Absent on rows and events that pre-date this field.
   */
  wallet: Schema.optionalKey(Schema.NullOr(WorkSessionWallet)),
  /**
   * Pending auto-create task prompt (M4.5). Set when the master proposes a
   * plan and `autoCreateTasks === 'ask'`. Cleared after user approves/discards.
   */
  pendingAutoCreatePrompt: Schema.optional(Schema.NullOr(AutoCreateTaskPrompt)).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  /**
   * Free-text session goal/intent. Lightweight counterpart to CMMD anchoring
   * ({@link OrchestrationThread.anchorKind}): persists "what this thread is
   * trying to achieve" for scratch threads that are not anchored to a CMMD
   * task. Null = no explicit goal. Re-injected as a context preamble on each
   * turn so the agent's intent survives compaction. Decodes to null on rows
   * that pre-date the goal column.
   */
  goal: Schema.NullOr(TrimmedNonEmptyString).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /**
   * Set while a title regeneration is running, cleared when it completes or is
   * superseded. Decodes to null on rows and journaled events that pre-date the
   * column, so existing threads keep loading.
   */
  titleRegeneration: Schema.NullOr(ThreadTitleRegeneration).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  /**
   * Rate-limit telemetry this thread's provider(s) have reported during real
   * turns. Never populated by a manufactured check - see
   * ThreadRateLimitSetPayload. Decodes to [] on rows that pre-date this field.
   */
  rateLimitStatus: Schema.optional(Schema.Array(ThreadProviderRateLimitStatus)).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
});
export type OrchestrationThread = typeof OrchestrationThread.Type;

export const OrchestrationReadModel = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProject),
  threads: Schema.Array(OrchestrationThread),
  updatedAt: IsoDateTime,
});
export type OrchestrationReadModel = typeof OrchestrationReadModel.Type;

export const OrchestrationProjectShell = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  availability: Schema.optional(ProjectAvailability),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  /**
   * Where this project's new threads start; absent/null means "use the app
   * setting". Optional rather than required-nullable because this is replayed
   * history and a wire contract -- every project created before this field
   * existed must still decode.
   */
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  cmmdRegistration: Schema.optional(Schema.NullOr(CmmdRegistration)),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProjectShell = typeof OrchestrationProjectShell.Type;

export const OrchestrationThreadShell = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  settledAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  settledOverride: Schema.optional(SettledOverride),
  session: Schema.NullOr(OrchestrationSession),
  latestUserMessageAt: Schema.NullOr(IsoDateTime),
  hasPendingApprovals: Schema.Boolean,
  hasPendingUserInput: Schema.Boolean,
  hasActionableProposedPlan: Schema.Boolean,
  /** CMMD anchor kind for this thread. Defaults to 'scratch' if not set. */
  anchorKind: CmmdAnchorKind.pipe(
    Schema.withDecodingDefault(Effect.succeed<CmmdAnchorKind>("scratch")),
  ),
  /** The CMMD entity ID. Null for scratch (local-only) threads. */
  anchorCmmdId: Schema.NullOr(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  linkedPullRequest: Schema.optionalKey(Schema.NullOr(LinkedPullRequest)),
  /** Thread kind — defaults to 'master' for back-compat with pre-M3 records. */
  kind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed<ThreadKind>("master"))),
  /** For worker threads, the master that spawned them. Null for master threads. */
  parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /** Orchestration status — defaults to 'idle' for back-compat with pre-M3 records. */
  status: ThreadStatus.pipe(Schema.withDecodingDefault(Effect.succeed<ThreadStatus>("idle"))),
  /** Worker role — null for masters and role-less workers. See {@link WorkerRole}. */
  workerRole: Schema.NullOr(WorkerRole).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /**
   * Free-text session goal/intent. Null when unset. Mirrors
   * {@link OrchestrationThread.goal} so the shell read-path (sidebar, header
   * chip) surfaces the persisted goal without requiring a full-thread load.
   */
  goal: Schema.NullOr(TrimmedNonEmptyString).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /**
   * Mirrors {@link OrchestrationThread.titleRegeneration} so the sidebar can
   * show a row as busy without loading the full thread.
   */
  titleRegeneration: Schema.NullOr(ThreadTitleRegeneration).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
});
export type OrchestrationThreadShell = typeof OrchestrationThreadShell.Type;

export const OrchestrationShellSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProjectShell),
  threads: Schema.Array(OrchestrationThreadShell),
  updatedAt: IsoDateTime,
});
export type OrchestrationShellSnapshot = typeof OrchestrationShellSnapshot.Type;

export const OrchestrationShellStreamEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("project-upserted"),
    sequence: NonNegativeInt,
    project: OrchestrationProjectShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("project-removed"),
    sequence: NonNegativeInt,
    projectId: ProjectId,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-upserted"),
    sequence: NonNegativeInt,
    thread: OrchestrationThreadShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-removed"),
    sequence: NonNegativeInt,
    threadId: ThreadId,
  }),
]);
export type OrchestrationShellStreamEvent = typeof OrchestrationShellStreamEvent.Type;

export const OrchestrationShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationShellSnapshot,
  }),
  OrchestrationShellStreamEvent,
]);
export type OrchestrationShellStreamItem = typeof OrchestrationShellStreamItem.Type;

export const OrchestrationSubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
  /**
   * When provided, the server skips the initial snapshot frame and replays this
   * thread's events after this sequence before streaming live events. A client
   * that already holds the thread, from its cache or from a previous
   * subscription, passes its watermark here so a resubscribe costs the catch-up
   * instead of the whole transcript. Overlapping events are deduped by sequence
   * on the client, so a small overlap is safe and a gap is not.
   */
  afterSequence: Schema.optionalKey(NonNegativeInt),
  /**
   * Requests an explicit marker after the subscription has emitted its initial
   * snapshot or catch-up replay and before it begins emitting live events. A
   * client uses it to know when it is caught up rather than guessing.
   */
  requestCompletionMarker: Schema.optionalKey(Schema.Boolean),
  /**
   * When provided, the snapshot frame, sent when `afterSequence` is missing or
   * the catch-up cannot be served, is windowed to the last `turnLimit`
   * user-anchored turns and carries `page` metadata. Absent means the snapshot
   * is the full thread, which is what every pre-pagination client gets. Live
   * events are unaffected either way.
   */
  turnLimit: Schema.optionalKey(PositiveInt),
});
export type OrchestrationSubscribeThreadInput = typeof OrchestrationSubscribeThreadInput.Type;

/**
 * Bounds a thread detail read to a window of recent turns. `turnLimit` counts
 * turns that hold a user message, so a window always contains the last N user
 * prompts plus whatever subagent or fan-out turns sit between them.
 * `beforeCursor` asks for the disjoint page of older turns strictly before a
 * cursor a previous read returned. A read with neither field gets the whole
 * thread, so pagination stays opt-in per request.
 */
export const OrchestrationThreadDetailWindow = Schema.Struct({
  turnLimit: Schema.optionalKey(PositiveInt),
  beforeCursor: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OrchestrationThreadDetailWindow = typeof OrchestrationThreadDetailWindow.Type;

/**
 * Reads one windowed page of a thread's detail directly over the RPC socket.
 *
 * The HTTP snapshot route and `subscribeThread`'s fallback snapshot both take
 * the same `turnLimit`/`beforeCursor` window, but neither can serve "load
 * earlier" on a connection with no HTTP endpoint: the fallback snapshot only
 * fires once, on subscribe. This method exists for exactly that case (a
 * relay-only connection), gated on the server's `threadDetailPageRpc`
 * capability so older servers are never sent a method they do not know.
 */
export const OrchestrationGetThreadDetailPageInput = Schema.Struct({
  threadId: ThreadId,
  turnLimit: Schema.optionalKey(PositiveInt),
  beforeCursor: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OrchestrationGetThreadDetailPageInput =
  typeof OrchestrationGetThreadDetailPageInput.Type;

/**
 * Page metadata for a windowed thread detail read. `beforeCursor` is opaque and
 * exclusive: passing it back returns the adjacent slice of older turns. `null`
 * means nothing older is left. `snapshotSequence` mirrors the top-level
 * sequence so a history page can be sequence-checked against live state before
 * a client merges it.
 */
export const OrchestrationThreadDetailPage = Schema.Struct({
  beforeCursor: Schema.NullOr(TrimmedNonEmptyString),
  hasMore: Schema.Boolean,
  snapshotSequence: NonNegativeInt,
});
export type OrchestrationThreadDetailPage = typeof OrchestrationThreadDetailPage.Type;

export const OrchestrationThreadDetailSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  thread: OrchestrationThread,
  /**
   * Present only on a windowed response. Absent on a full snapshot, which a
   * client reads as "this thread is fully loaded".
   */
  page: Schema.optional(OrchestrationThreadDetailPage),
});
export type OrchestrationThreadDetailSnapshot = typeof OrchestrationThreadDetailSnapshot.Type;

export const ProjectCreateCommand = Schema.Struct({
  type: Schema.Literal("project.create"),
  commandId: CommandId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  /**
   * Where this project's new threads start; absent/null means "use the app
   * setting". Optional rather than required-nullable because this is replayed
   * history and a wire contract -- every project created before this field
   * existed must still decode.
   */
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  cmmdRegistration: Schema.optional(Schema.NullOr(CmmdRegistration)),
  createdAt: IsoDateTime,
});

const ProjectMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("project.meta.update"),
  commandId: CommandId,
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  /**
   * Where this project's new threads start; absent/null means "use the app
   * setting". Optional rather than required-nullable because this is replayed
   * history and a wire contract -- every project created before this field
   * existed must still decode.
   */
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  cmmdRegistration: Schema.optional(Schema.NullOr(CmmdRegistration)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
});

const ProjectDeleteCommand = Schema.Struct({
  type: Schema.Literal("project.delete"),
  commandId: CommandId,
  projectId: ProjectId,
});

/**
 * The CMMD Work Session id a command may carry (forge#3466 review, nit 9).
 * CMMD's capability broker requires 1 to 256 characters; bounding it here
 * rejects a malformed id at the door instead of letting decide-time code find
 * out. Command-only: the read model and its events keep decoding a plain
 * string so a row or a journaled event written before this bound existed
 * still loads.
 */
const CommandWorkSessionId = TrimmedNonEmptyString.check(Schema.isMaxLength(256));

const ThreadCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.create"),
  commandId: CommandId,
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  anchorKind: Schema.optional(CmmdAnchorKind),
  anchorCmmdId: Schema.optional(Schema.NullOr(Schema.String)),
  /**
   * The CMMD Work Session id this thread's root turn is kicked for, when CMMD
   * is the caller. Omit for a person-started thread. See
   * {@link OrchestrationThread.workSessionId}.
   */
  workSessionId: Schema.optional(Schema.NullOr(CommandWorkSessionId)),
  /**
   * The wallet of the Work Session this thread was created for. It sets the
   * thread's starting {@link OrchestrationThread.wallet}. A Retry replays this
   * command's first receipt, so the kick `thread.turn.start` is what moves the
   * wallet for a later Work Session. See {@link WorkSessionWallet}.
   */
  wallet: Schema.optional(WorkSessionWallet),
  /** Optional standing goal persisted atomically with the thread. */
  goal: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  /** Optional canonical pull request identity persisted atomically with the thread. */
  linkedPullRequest: Schema.optional(Schema.NullOr(LinkedPullRequest)),
  /** Thread kind — omit or 'master' for a top-level thread; 'worker' for a spawned sub-thread. */
  kind: Schema.optional(ThreadKind),
  /** When kind='worker', the master thread that spawned this worker. */
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  /** Initial orchestration status — defaults to 'idle' when omitted. */
  status: Schema.optional(ThreadStatus),
  /** Optional worker role. Ignored for master threads. */
  workerRole: Schema.optional(Schema.NullOr(WorkerRole)),
  createdAt: IsoDateTime,
});

const ThreadDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.delete"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadArchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.archive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadSettleCommand = Schema.Struct({
  type: Schema.Literal("thread.settle"),
  commandId: CommandId,
  threadId: ThreadId,
  settledOverride: Schema.optional(SettledOverride),
});

const ThreadUnsettleCommand = Schema.Struct({
  type: Schema.Literal("thread.unsettle"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnarchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.unarchive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.meta.update"),
  commandId: CommandId,
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  /**
   * Ask the server to regenerate this thread's title from its conversation.
   * Mutually exclusive with `title` (see the filter below): regeneration is a
   * request about the *current* title, so a command that also sets one is
   * contradictory and whichever the reducer applied second would silently win.
   */
  regenerateTitle: Schema.optional(Schema.Literal(true)),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  anchorKind: Schema.optional(CmmdAnchorKind),
  anchorCmmdId: Schema.optional(Schema.NullOr(Schema.String)),
  /** Update the orchestration status (e.g. idle → running → needs-review). */
  status: Schema.optional(ThreadStatus),
  /** Set/clear parent thread (used when re-parenting or promoting a thread). */
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  /**
   * Set or clear the free-text session goal. Pass a trimmed non-empty string to
   * set it, `null` to clear it, or omit to leave it unchanged. Clients MUST
   * trim and coerce empty input to `null` before dispatch (TrimmedNonEmptyString
   * rejects empty/whitespace-padded values).
   */
  goal: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  /** Set/clear the durable PR identity; omit to leave the existing relation unchanged. */
  linkedPullRequest: Schema.optional(Schema.NullOr(LinkedPullRequest)),
}).check(
  Schema.makeFilter(
    (input) =>
      // Narrow on purpose: this rejects only the contradictory *pair*. Every
      // other field on this command stays freely combinable with either one.
      !(input.title !== undefined && input.regenerateTitle === true) ||
      new SchemaIssue.InvalidValue(Option.some(input.title), {
        message: "title and regenerateTitle cannot be specified together",
      }),
    { identifier: "ThreadMetaUpdateTitleExclusivity" },
  ),
);

const ThreadRuntimeModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.runtime-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  createdAt: IsoDateTime,
});

const ThreadInteractionModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.interaction-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});

const ThreadTurnStartBootstrapCreateThread = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  // FORGE-189: CMMD task anchor forwarded into the synthesized `thread.create`.
  // Without these, auto-start launches from a CMMD task created threads with
  // `anchorKind: "scratch"` and the Task context strip never rendered.
  anchorKind: Schema.optional(CmmdAnchorKind),
  anchorCmmdId: Schema.optional(Schema.NullOr(Schema.String)),
  /** Optional standing goal persisted atomically with the bootstrapped thread. */
  goal: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  /** Optional canonical pull request identity persisted with the synthesized thread.create. */
  linkedPullRequest: Schema.optional(Schema.NullOr(LinkedPullRequest)),
  createdAt: IsoDateTime,
});

const ThreadTurnStartBootstrapPrepareWorktree = Schema.Struct({
  /** Authoritative Forge project identity. The server resolves its workspaceRoot. */
  projectId: Schema.optional(ProjectId),
  /** Legacy client-supplied checkout path. Ignored when projectId is present. */
  projectCwd: Schema.optional(TrimmedNonEmptyString),
  // Null means the server resolves the repository's default branch itself.
  baseBranch: Schema.NullOr(TrimmedNonEmptyString),
  branch: Schema.optional(TrimmedNonEmptyString),
  /** Fetch the named base branch from origin before creating the worktree. */
  startFromOrigin: Schema.optional(Schema.Boolean),
});

const ThreadTurnStartBootstrap = Schema.Struct({
  createThread: Schema.optional(ThreadTurnStartBootstrapCreateThread),
  prepareWorktree: Schema.optional(ThreadTurnStartBootstrapPrepareWorktree),
  runSetupScript: Schema.optional(Schema.Boolean),
});

export type ThreadTurnStartBootstrap = typeof ThreadTurnStartBootstrap.Type;

export const ThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(ChatAttachment),
  }),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  /**
   * CMMD task ID to inject context preamble into the first turn. Server-side only.
   * Shorthand alias: implies anchorKind='task' when set.
   */
  cmmdTaskId: Schema.optional(Schema.String),
  /**
   * Full anchor kind for this thread turn. When anchorKind is provided alongside
   * anchorCmmdId, these take precedence over the cmmdTaskId shorthand.
   */
  anchorKind: Schema.optional(CmmdAnchorKind),
  /** The CMMD entity ID for the anchor (null for scratch threads). */
  anchorCmmdId: Schema.optional(Schema.NullOr(Schema.String)),
  /** Thread kind — informs downstream runtime whether to treat this as master/worker. */
  kind: Schema.optional(ThreadKind),
  /** For worker threads, the master that spawned them. */
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  createdAt: IsoDateTime,
  /**
   * The CMMD Work Session id this turn belongs to (forge#3466 review, CMMD's
   * `RootThreadTurnStartCommand.workSessionId`, refs CMMD#5600). A Retry
   * reuses the thread and `thread.create` replays its first receipt, so this
   * is the one command that can tell Forge which session is running on the
   * thread now; the decider rebinds `OrchestrationThread.workSessionId` to it
   * when it differs from the thread's current one. Omit for a person-started
   * turn, which leaves the thread's existing binding untouched.
   */
  workSessionId: Schema.optional(Schema.NullOr(CommandWorkSessionId)),
  /**
   * The wallet CMMD stamped on the Work Session this turn belongs to
   * (CMMD#5917). The provider session started for this turn keeps it, and a
   * later turn that omits it (a person typing into the thread) inherits it
   * rather than dropping to the personal login. See {@link WorkSessionWallet}.
   */
  wallet: Schema.optional(WorkSessionWallet),
});

const ClientThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(Schema.Union([UploadChatAttachment, ChatAttachment])),
  }),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  /**
   * CMMD task ID — triggers server-side context preamble injection before the turn is sent.
   * Shorthand alias: implies anchorKind='task' when set. Kept for backward compatibility.
   */
  cmmdTaskId: Schema.optional(Schema.String),
  /**
   * Full anchor kind for this thread turn. Takes precedence over cmmdTaskId when both
   * are provided. Use 'scratch' for local threads with no CMMD anchor.
   */
  anchorKind: Schema.optional(CmmdAnchorKind),
  /** The CMMD entity ID for the anchor. Null for scratch threads. */
  anchorCmmdId: Schema.optional(Schema.NullOr(Schema.String)),
  /** Thread kind — informs downstream runtime whether to treat this as master/worker. */
  kind: Schema.optional(ThreadKind),
  /** For worker threads, the master that spawned them. */
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  createdAt: IsoDateTime,
  /**
   * The CMMD Work Session id this turn belongs to. This is the field CMMD's
   * kick actually posts to `POST /api/orchestration/dispatch`, which decodes
   * against this struct (`ClientOrchestrationCommand`), not the server-only
   * `ThreadTurnStartCommand` — see that type's matching field for the full
   * doc comment (forge#3466 review).
   */
  workSessionId: Schema.optional(Schema.NullOr(CommandWorkSessionId)),
  /**
   * The wallet CMMD stamped on this turn's Work Session. CMMD's kick posts
   * this struct, so this is where the field arrives; see the matching field
   * on `ThreadTurnStartCommand`.
   */
  wallet: Schema.optional(WorkSessionWallet),
});

const ThreadTurnInterruptCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.interrupt"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadApprovalRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.approval.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  createdAt: IsoDateTime,
});

const ThreadUserInputRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.user-input.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  attachmentsByQuestionId: Schema.optional(UserInputAttachments),
  createdAt: IsoDateTime,
});

const ThreadCheckpointRevertCommand = Schema.Struct({
  type: Schema.Literal("thread.checkpoint.revert"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

/**
 * M4.5 — respond to a pending auto-create task prompt.
 * `decision: 'approve'` triggers task creation + worker spawning.
 * `decision: 'discard'` clears the pending prompt without creating tasks.
 */
export const ThreadPlanApprovalRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.plan-approval.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  promptId: AutoCreateTaskPromptId,
  decision: Schema.Literals(["approve", "discard"]),
  createdAt: IsoDateTime,
});
export type ThreadPlanApprovalRespondCommand = typeof ThreadPlanApprovalRespondCommand.Type;

const ThreadSessionStopCommand = Schema.Struct({
  type: Schema.Literal("thread.session.stop"),
  commandId: CommandId,
  threadId: ThreadId,
  preserveResumeState: Schema.optional(Schema.Boolean),
  createdAt: IsoDateTime,
});

const DispatchableClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectScriptUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadSettleCommand,
  ThreadUnsettleCommand,
  ThreadUnarchiveCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ThreadTurnStartCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadCheckpointRevertCommand,
  ThreadSessionStopCommand,
  ThreadPlanApprovalRespondCommand,
]);
export type DispatchableClientOrchestrationCommand =
  typeof DispatchableClientOrchestrationCommand.Type;

export const ClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectScriptUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadSettleCommand,
  ThreadUnsettleCommand,
  ThreadUnarchiveCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ClientThreadTurnStartCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadCheckpointRevertCommand,
  ThreadSessionStopCommand,
  ThreadPlanApprovalRespondCommand,
]);
export type ClientOrchestrationCommand = typeof ClientOrchestrationCommand.Type;

const ThreadSessionSetCommand = Schema.Struct({
  type: Schema.Literal("thread.session.set"),
  commandId: CommandId,
  threadId: ThreadId,
  session: OrchestrationSession,
  expectedActiveTurnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantDeltaCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.delta"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  delta: Schema.String,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadProposedPlanUpsertCommand = Schema.Struct({
  type: Schema.Literal("thread.proposed-plan.upsert"),
  commandId: CommandId,
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
  createdAt: IsoDateTime,
});

const ThreadTurnDiffCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.diff.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  completedAt: IsoDateTime,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.optional(MessageId),
  checkpointTurnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const ThreadActivityAppendCommand = Schema.Struct({
  type: Schema.Literal("thread.activity.append"),
  commandId: CommandId,
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
  createdAt: IsoDateTime,
});

const ThreadRateLimitSetCommand = Schema.Struct({
  type: Schema.Literal("thread.rate-limit.set"),
  commandId: CommandId,
  threadId: ThreadId,
  provider: Schema.Literals(["claude", "codex"]),
  providerAccountId: Schema.optional(Schema.NullOr(ProviderAccountId)).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  window: Schema.Literals(["five_hour", "seven_day"]),
  status: Schema.Literals(["allowed", "allowed_warning", "rejected"]),
  usedPercent: Schema.Number,
  resetsAt: IsoDateTime,
  createdAt: IsoDateTime,
});

const ThreadRevertCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.revert.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const ThreadPlanApprovalRequestCommand = Schema.Struct({
  type: Schema.Literal("thread.plan-approval.request"),
  commandId: CommandId,
  threadId: ThreadId,
  prompt: AutoCreateTaskPrompt,
  createdAt: IsoDateTime,
});

const ThreadReplicaHydrateCommand = Schema.Struct({
  type: Schema.Literal("thread.replica.hydrate"),
  commandId: CommandId,
  runtimeId: TrimmedNonEmptyString,
  generationId: TrimmedNonEmptyString,
  threadId: ThreadId,
  thread: OrchestrationThread,
  createdAt: IsoDateTime,
});

/**
 * Emitted by the server when a title regeneration finishes. `requestId` is the
 * originating `thread.meta.update` commandId; the decider applies the title
 * only when that request is still the current one, so a slow regeneration
 * cannot overwrite a title the user set (or a newer regeneration) meanwhile.
 * Omitting `title` completes the request without changing the title — that is
 * how failures, no-op results, and interrupted-on-restart sweeps clear the flag.
 */
const ThreadTitleRegenerationCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.title.regeneration.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: CommandId,
  title: Schema.optional(TrimmedNonEmptyString),
});

const InternalOrchestrationCommand = Schema.Union([
  ThreadSessionSetCommand,
  ThreadMessageAssistantDeltaCommand,
  ThreadMessageAssistantCompleteCommand,
  ThreadProposedPlanUpsertCommand,
  ThreadTurnDiffCompleteCommand,
  ThreadActivityAppendCommand,
  ThreadRateLimitSetCommand,
  ThreadRevertCompleteCommand,
  ThreadPlanApprovalRequestCommand,
  ThreadReplicaHydrateCommand,
  ThreadTitleRegenerationCompleteCommand,
]);
export type InternalOrchestrationCommand = typeof InternalOrchestrationCommand.Type;

export const OrchestrationCommand = Schema.Union([
  DispatchableClientOrchestrationCommand,
  InternalOrchestrationCommand,
]);
export type OrchestrationCommand = typeof OrchestrationCommand.Type;

export const OrchestrationEventType = Schema.Literals([
  "project.created",
  "project.meta-updated",
  "project.deleted",
  "thread.created",
  "thread.deleted",
  "thread.archived",
  "thread.settled",
  "thread.unsettled",
  "thread.unarchived",
  "thread.meta-updated",
  "thread.runtime-mode-set",
  "thread.interaction-mode-set",
  "thread.message-sent",
  "thread.turn-start-requested",
  "thread.turn-interrupt-requested",
  "thread.approval-response-requested",
  "thread.user-input-response-requested",
  "thread.checkpoint-revert-requested",
  "thread.reverted",
  "thread.session-stop-requested",
  "thread.session-set",
  "thread.proposed-plan-upserted",
  "thread.turn-diff-completed",
  "thread.activity-appended",
  "thread.worker-spawned",
  "thread.plan-approval-requested",
  "thread.plan-approval-responded",
  "thread.replica-hydrated",
  "thread.rate-limit-set",
]);
export type OrchestrationEventType = typeof OrchestrationEventType.Type;

export const OrchestrationAggregateKind = Schema.Literals(["project", "thread"]);
export type OrchestrationAggregateKind = typeof OrchestrationAggregateKind.Type;
export const OrchestrationActorKind = Schema.Literals(["client", "server", "provider"]);

export const ProjectCreatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  /**
   * Where this project's new threads start; absent/null means "use the app
   * setting". Optional rather than required-nullable because this is replayed
   * history and a wire contract -- every project created before this field
   * existed must still decode.
   */
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  cmmdRegistration: Schema.optional(Schema.NullOr(CmmdRegistration)),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ProjectMetaUpdatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  /**
   * Where this project's new threads start; absent/null means "use the app
   * setting". Optional rather than required-nullable because this is replayed
   * history and a wire contract -- every project created before this field
   * existed must still decode.
   */
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  cmmdRegistration: Schema.optional(Schema.NullOr(CmmdRegistration)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
  updatedAt: IsoDateTime,
});

export const ProjectDeletedPayload = Schema.Struct({
  projectId: ProjectId,
  deletedAt: IsoDateTime,
});

export const ThreadCreatedPayload = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  settledAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  settledOverride: Schema.optional(SettledOverride),
  anchorKind: CmmdAnchorKind.pipe(
    Schema.withDecodingDefault(Effect.succeed<CmmdAnchorKind>("scratch")),
  ),
  anchorCmmdId: Schema.NullOr(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /** See {@link OrchestrationThread.workSessionId}. */
  workSessionId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /** See {@link OrchestrationThread.workSessionBoundAt}. Set to createdAt when workSessionId is present. */
  workSessionBoundAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /** See {@link OrchestrationThread.wallet}. */
  wallet: Schema.optionalKey(Schema.NullOr(WorkSessionWallet)),
  goal: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  linkedPullRequest: Schema.optionalKey(Schema.NullOr(LinkedPullRequest)),
  /** Thread kind — defaults to 'master' for back-compat. */
  kind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed<ThreadKind>("master"))),
  /** For worker threads, the master that spawned them. */
  parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  /** Orchestration status — defaults to 'idle'. */
  status: ThreadStatus.pipe(Schema.withDecodingDefault(Effect.succeed<ThreadStatus>("idle"))),
  /** Worker role assigned at creation time. Null for masters and role-less workers. */
  workerRole: Schema.NullOr(WorkerRole).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
});

export const ThreadDeletedPayload = Schema.Struct({
  threadId: ThreadId,
  deletedAt: IsoDateTime,
});

export const ThreadArchivedPayload = Schema.Struct({
  threadId: ThreadId,
  archivedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadSettledPayload = Schema.Struct({
  threadId: ThreadId,
  settledAt: IsoDateTime,
  settledOverride: SettledOverride,
  updatedAt: IsoDateTime,
});

export const ThreadUnsettledPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadUnarchivedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadMetaUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  anchorKind: Schema.optional(CmmdAnchorKind),
  anchorCmmdId: Schema.optional(Schema.NullOr(Schema.String)),
  status: Schema.optional(ThreadStatus),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  workerRole: Schema.optional(Schema.NullOr(WorkerRole)),
  /**
   * Rebinds {@link OrchestrationThread.workSessionId} to a later Work Session
   * on the same thread (forge#3466 review, blocker 1). Only ever emitted with
   * a non-null value: the decider never clears the binding through this
   * event, so omitted means unchanged. Kept as a plain string (not the
   * bounded `CommandWorkSessionId` the command uses) because this is a
   * journaled event payload that must keep decoding old rows forever.
   */
  workSessionId: Schema.optional(Schema.NullOr(Schema.String)),
  /**
   * ISO timestamp of the rebind — `command.createdAt` when a workSessionId
   * rebind is emitted (forge#3597). Present whenever `workSessionId` is
   * present. Kept as a plain string so old event rows decode forever.
   */
  workSessionBoundAt: Schema.optional(Schema.String),
  /**
   * Moves {@link OrchestrationThread.wallet} to the wallet a later kick
   * named. Omitted means unchanged.
   */
  wallet: Schema.optional(WorkSessionWallet),
  /** Set/clear the free-text session goal; omitted = unchanged. */
  goal: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  linkedPullRequest: Schema.optional(Schema.NullOr(LinkedPullRequest)),
  /** Present on the event that starts a regeneration. */
  regenerateTitle: Schema.optional(Schema.Literal(true)),
  /**
   * The title at the moment regeneration was requested. The reactor refuses to
   * apply its result if the thread has moved off this title, which is what
   * makes a manual rename win a race against an in-flight regeneration.
   */
  previousTitle: Schema.optional(TrimmedNonEmptyString),
  /** Set to stamp an in-flight regeneration, `null` to clear it. */
  titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
  updatedAt: IsoDateTime,
});

export const ThreadRuntimeModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  updatedAt: IsoDateTime,
});

export const ThreadInteractionModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  updatedAt: IsoDateTime,
});

export const ThreadMessageSentPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadTurnStartRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  /**
   * The wallet this turn runs on, fixed when the turn was accepted: the one
   * the `thread.turn.start` named, else the thread's wallet at that moment.
   * The provider reactor hands it to the session it starts. Absent on older
   * events and on threads no kick ever gave a wallet.
   */
  wallet: Schema.optional(WorkSessionWallet),
  createdAt: IsoDateTime,
});

export const ThreadTurnInterruptRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

export const ThreadApprovalResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  createdAt: IsoDateTime,
});

const ThreadUserInputResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  attachmentsByQuestionId: Schema.optional(UserInputAttachments),
  createdAt: IsoDateTime,
});

export const ThreadCheckpointRevertRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

/** M4.5 — payload for the provider-owned plan approval request event. */
export const ThreadPlanApprovalRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  prompt: AutoCreateTaskPrompt,
  createdAt: IsoDateTime,
});
export type ThreadPlanApprovalRequestedPayload = typeof ThreadPlanApprovalRequestedPayload.Type;

/** M4.5 — payload for the plan-approval.responded event. */
export const ThreadPlanApprovalRespondedPayload = Schema.Struct({
  threadId: ThreadId,
  promptId: AutoCreateTaskPromptId,
  decision: Schema.Literals(["approve", "discard"]),
  createdAt: IsoDateTime,
});
export type ThreadPlanApprovalRespondedPayload = typeof ThreadPlanApprovalRespondedPayload.Type;

export const ThreadRevertedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
});

export const ThreadSessionStopRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  preserveResumeState: Schema.optional(Schema.Boolean),
  createdAt: IsoDateTime,
});

export const ThreadSessionSetPayload = Schema.Struct({
  threadId: ThreadId,
  session: OrchestrationSession,
});

export const ThreadProposedPlanUpsertedPayload = Schema.Struct({
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
});

export const ThreadTurnDiffCompletedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});

export const ThreadActivityAppendedPayload = Schema.Struct({
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
});

/**
 * A thread's active provider account just reported rate-limit telemetry
 * during a real turn (the genuine SDK/CLI's own signal - never a manufactured
 * check). "window" is the reporting window (five_hour/seven_day for Claude,
 * the closest equivalent Codex reports); a window not present in the latest
 * signal keeps whatever value the client already has, so one event never
 * blanks out data for the other window.
 */
export const ThreadRateLimitSetPayload = Schema.Struct({
  threadId: ThreadId,
  provider: Schema.Literals(["claude", "codex"]),
  /** The account this telemetry describes; null when the thread has none bound. */
  providerAccountId: Schema.optional(Schema.NullOr(ProviderAccountId)).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  window: Schema.Literals(["five_hour", "seven_day"]),
  status: Schema.Literals(["allowed", "allowed_warning", "rejected"]),
  usedPercent: Schema.Number,
  resetsAt: IsoDateTime,
});
export type ThreadRateLimitSetPayload = typeof ThreadRateLimitSetPayload.Type;

export const ThreadReplicaHydratedPayload = Schema.Struct({
  runtimeId: TrimmedNonEmptyString,
  generationId: TrimmedNonEmptyString,
  threadId: ThreadId,
  thread: OrchestrationThread,
  applied: Schema.Boolean,
});
export type ThreadReplicaHydratedPayload = typeof ThreadReplicaHydratedPayload.Type;

/**
 * Emitted when a master spawns a worker thread. Drives sidebar reactivity so
 * web clients can invalidate their thread tree without full refetch. This is
 * the thin contract layer — the actual worker thread record is created via
 * `thread.created` with `kind: "worker"` shortly before or after this event.
 */
export const ThreadWorkerSpawnedPayload = Schema.Struct({
  masterThreadId: ThreadId,
  workerThreadId: ThreadId,
  branch: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  slug: TrimmedNonEmptyString,
  spawnedAt: IsoDateTime,
  /** Optional role assigned to the worker. Null → UI shows neutral "Worker" badge. */
  workerRole: Schema.NullOr(WorkerRole).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
});
export type ThreadWorkerSpawnedPayload = typeof ThreadWorkerSpawnedPayload.Type;

export const OrchestrationEventMetadata = Schema.Struct({
  providerTurnId: Schema.optional(TrimmedNonEmptyString),
  providerItemId: Schema.optional(ProviderItemId),
  adapterKey: Schema.optional(TrimmedNonEmptyString),
  requestId: Schema.optional(ApprovalRequestId),
  ingestedAt: Schema.optional(IsoDateTime),
});
export type OrchestrationEventMetadata = typeof OrchestrationEventMetadata.Type;

const EventBaseFields = {
  sequence: NonNegativeInt,
  eventId: EventId,
  aggregateKind: OrchestrationAggregateKind,
  aggregateId: Schema.Union([ProjectId, ThreadId]),
  occurredAt: IsoDateTime,
  commandId: Schema.NullOr(CommandId),
  causationEventId: Schema.NullOr(EventId),
  correlationId: Schema.NullOr(CommandId),
  metadata: OrchestrationEventMetadata,
} as const;

export const OrchestrationEvent = Schema.Union([
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.created"),
    payload: ProjectCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.meta-updated"),
    payload: ProjectMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.deleted"),
    payload: ProjectDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.created"),
    payload: ThreadCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.deleted"),
    payload: ThreadDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.archived"),
    payload: ThreadArchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.settled"),
    payload: ThreadSettledPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unsettled"),
    payload: ThreadUnsettledPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unarchived"),
    payload: ThreadUnarchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.meta-updated"),
    payload: ThreadMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.runtime-mode-set"),
    payload: ThreadRuntimeModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.interaction-mode-set"),
    payload: ThreadInteractionModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.message-sent"),
    payload: ThreadMessageSentPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-start-requested"),
    payload: ThreadTurnStartRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-interrupt-requested"),
    payload: ThreadTurnInterruptRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.approval-response-requested"),
    payload: ThreadApprovalResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.user-input-response-requested"),
    payload: ThreadUserInputResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.checkpoint-revert-requested"),
    payload: ThreadCheckpointRevertRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.plan-approval-requested"),
    payload: ThreadPlanApprovalRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.plan-approval-responded"),
    payload: ThreadPlanApprovalRespondedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.reverted"),
    payload: ThreadRevertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-stop-requested"),
    payload: ThreadSessionStopRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-set"),
    payload: ThreadSessionSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.proposed-plan-upserted"),
    payload: ThreadProposedPlanUpsertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-diff-completed"),
    payload: ThreadTurnDiffCompletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.activity-appended"),
    payload: ThreadActivityAppendedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.worker-spawned"),
    payload: ThreadWorkerSpawnedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.replica-hydrated"),
    payload: ThreadReplicaHydratedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.rate-limit-set"),
    payload: ThreadRateLimitSetPayload,
  }),
]);
export type OrchestrationEvent = typeof OrchestrationEvent.Type;

export const OrchestrationThreadStreamItem = Schema.Union([
  /**
   * Catch-up completion marker, emitted only when the subscriber asked for one
   * with `requestCompletionMarker`. It arrives after the snapshot or the
   * replayed catch-up and before the first live event.
   */
  Schema.Struct({
    kind: Schema.Literal("synchronized"),
  }),
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationThreadDetailSnapshot,
  }),
  Schema.Struct({
    kind: Schema.Literal("event"),
    event: OrchestrationEvent,
  }),
]);
export type OrchestrationThreadStreamItem = typeof OrchestrationThreadStreamItem.Type;

export const OrchestrationCommandReceiptStatus = Schema.Literals(["accepted", "rejected"]);
export type OrchestrationCommandReceiptStatus = typeof OrchestrationCommandReceiptStatus.Type;

export const TurnCountRange = Schema.Struct({
  fromTurnCount: NonNegativeInt,
  toTurnCount: NonNegativeInt,
}).check(
  Schema.makeFilter(
    (input) =>
      input.fromTurnCount <= input.toTurnCount ||
      new SchemaIssue.InvalidValue(Option.some(input.fromTurnCount), {
        message: "fromTurnCount must be less than or equal to toTurnCount",
      }),
    { identifier: "OrchestrationTurnDiffRange" },
  ),
);

export const ThreadTurnDiff = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    diff: Schema.String,
  }),
  { unsafePreserveChecks: true },
);

export const ProviderSessionRuntimeStatus = Schema.Literals([
  "starting",
  "running",
  "stopped",
  "error",
]);
export type ProviderSessionRuntimeStatus = typeof ProviderSessionRuntimeStatus.Type;

const ProjectionThreadTurnStatus = Schema.Literals([
  "running",
  "completed",
  "interrupted",
  "error",
]);
export type ProjectionThreadTurnStatus = typeof ProjectionThreadTurnStatus.Type;

const ProjectionCheckpointRow = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type ProjectionCheckpointRow = typeof ProjectionCheckpointRow.Type;

export const ProjectionPendingApprovalStatus = Schema.Literals(["pending", "resolved"]);
export type ProjectionPendingApprovalStatus = typeof ProjectionPendingApprovalStatus.Type;

export const ProjectionPendingApprovalDecision = Schema.NullOr(ProviderApprovalDecision);
export type ProjectionPendingApprovalDecision = typeof ProjectionPendingApprovalDecision.Type;

export const DispatchResult = Schema.Struct({
  sequence: NonNegativeInt,
});
export type DispatchResult = typeof DispatchResult.Type;

export const OrchestrationGetTurnDiffInput = TurnCountRange.mapFields(
  Struct.assign({ threadId: ThreadId }),
  { unsafePreserveChecks: true },
);
export type OrchestrationGetTurnDiffInput = typeof OrchestrationGetTurnDiffInput.Type;

export const OrchestrationGetTurnDiffResult = ThreadTurnDiff;
export type OrchestrationGetTurnDiffResult = typeof OrchestrationGetTurnDiffResult.Type;

export const OrchestrationGetFullThreadDiffInput = Schema.Struct({
  threadId: ThreadId,
  toTurnCount: NonNegativeInt,
});
export type OrchestrationGetFullThreadDiffInput = typeof OrchestrationGetFullThreadDiffInput.Type;

export const OrchestrationGetFullThreadDiffResult = ThreadTurnDiff;
export type OrchestrationGetFullThreadDiffResult = typeof OrchestrationGetFullThreadDiffResult.Type;

export const OrchestrationReplayEventsInput = Schema.Struct({
  fromSequenceExclusive: NonNegativeInt,
});
export type OrchestrationReplayEventsInput = typeof OrchestrationReplayEventsInput.Type;

const OrchestrationReplayEventsResult = Schema.Array(OrchestrationEvent);
export type OrchestrationReplayEventsResult = typeof OrchestrationReplayEventsResult.Type;

/**
 * Which side of the conversation a search hit came from. `null`
 * `messageCreatedAt` on a match means it came from the thread's own metadata
 * (its title or its branch) rather than from a message.
 */
export const OrchestrationThreadSearchSource = Schema.Literals(["user", "assistant"]);
export type OrchestrationThreadSearchSource = typeof OrchestrationThreadSearchSource.Type;

/**
 * The server's SQLite client is synchronous and single-connection, so both the
 * scan input and the response size are bounded: a search must not be able to
 * monopolize the connection every keystroke.
 */
export const OrchestrationSearchThreadsInput = Schema.Struct({
  query: TrimmedString.check(Schema.isMinLength(2), Schema.isMaxLength(200)),
  limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 }))),
});
export type OrchestrationSearchThreadsInput = typeof OrchestrationSearchThreadsInput.Type;

export const OrchestrationThreadSearchMatch = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  source: OrchestrationThreadSearchSource,
  snippet: Schema.String.check(Schema.isMaxLength(240)),
  messageCreatedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationThreadSearchMatch = typeof OrchestrationThreadSearchMatch.Type;

export const OrchestrationSearchThreadsResult = Schema.Struct({
  matches: Schema.Array(OrchestrationThreadSearchMatch),
});
export type OrchestrationSearchThreadsResult = typeof OrchestrationSearchThreadsResult.Type;

// ── Agent protocol (master ↔ worker) ─────────────────────────────────────────
//
// Typed messages passed between a master thread and its workers. Carried over
// the existing WebSocket pub/sub infrastructure. Handled server-side by
// `agentProtocolRouter.ts`; rendered client-side with rich per-kind UI in M4.

const ProgressPercent = NonNegativeInt.check(Schema.isLessThanOrEqualTo(100));

export const AgentProtocolProgress = Schema.Struct({
  kind: Schema.Literal("PROGRESS"),
  percent: ProgressPercent,
  note: Schema.String,
});
export type AgentProtocolProgress = typeof AgentProtocolProgress.Type;

export const AgentProtocolDone = Schema.Struct({
  kind: Schema.Literal("DONE"),
  branch: TrimmedNonEmptyString,
  commitSha: TrimmedNonEmptyString,
  summary: Schema.String,
});
export type AgentProtocolDone = typeof AgentProtocolDone.Type;

export const AgentProtocolStuck = Schema.Struct({
  kind: Schema.Literal("STUCK"),
  reason: TrimmedNonEmptyString,
  needsFromMaster: Schema.String,
});
export type AgentProtocolStuck = typeof AgentProtocolStuck.Type;

export const AgentProtocolNeedReview = Schema.Struct({
  kind: Schema.Literal("NEED_REVIEW"),
  question: TrimmedNonEmptyString,
  options: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
});
export type AgentProtocolNeedReview = typeof AgentProtocolNeedReview.Type;

export const AgentProtocolVisibilityRequest = Schema.Struct({
  kind: Schema.Literal("VISIBILITY_REQUEST"),
  targetWorkerId: ThreadId,
  reason: TrimmedNonEmptyString,
});
export type AgentProtocolVisibilityRequest = typeof AgentProtocolVisibilityRequest.Type;

export const AgentProtocolMessage = Schema.Union([
  AgentProtocolProgress,
  AgentProtocolDone,
  AgentProtocolStuck,
  AgentProtocolNeedReview,
  AgentProtocolVisibilityRequest,
]);
export type AgentProtocolMessage = typeof AgentProtocolMessage.Type;

export const AgentProtocolMessageKind = Schema.Literals([
  "PROGRESS",
  "DONE",
  "STUCK",
  "NEED_REVIEW",
  "VISIBILITY_REQUEST",
]);
export type AgentProtocolMessageKind = typeof AgentProtocolMessageKind.Type;

export const AgentProtocolEnvelope = Schema.Struct({
  fromThreadId: ThreadId,
  toThreadId: ThreadId,
  timestamp: IsoDateTime,
  message: AgentProtocolMessage,
});
export type AgentProtocolEnvelope = typeof AgentProtocolEnvelope.Type;

export const OrchestrationRpcSchemas = {
  dispatchCommand: {
    input: ClientOrchestrationCommand,
    output: DispatchResult,
  },
  getTurnDiff: {
    input: OrchestrationGetTurnDiffInput,
    output: OrchestrationGetTurnDiffResult,
  },
  getFullThreadDiff: {
    input: OrchestrationGetFullThreadDiffInput,
    output: OrchestrationGetFullThreadDiffResult,
  },
  replayEvents: {
    input: OrchestrationReplayEventsInput,
    output: OrchestrationReplayEventsResult,
  },
  subscribeThread: {
    input: OrchestrationSubscribeThreadInput,
    output: OrchestrationThreadStreamItem,
  },
  subscribeShell: {
    input: Schema.Struct({}),
    output: OrchestrationShellStreamItem,
  },
  /**
   * The archive's own read. `subscribeShell` carries archived threads too, but
   * only as part of the live navigation stream a client keeps open, so a client
   * that wants the archive on demand had no way to ask for it. Same snapshot
   * shape, restricted to threads with `archivedAt` set.
   */
  getArchivedShellSnapshot: {
    input: Schema.Struct({}),
    output: OrchestrationShellSnapshot,
  },
  /**
   * Thread search over what the server already holds.
   *
   * A client that cannot ask this question falls back to filtering the threads
   * it has in memory by title, which finds nothing for a word that only ever
   * appeared in a message body and reports that as "no results" rather than as
   * a missing feature.
   */
  searchThreads: {
    input: OrchestrationSearchThreadsInput,
    output: OrchestrationSearchThreadsResult,
  },
  getThreadDetailPage: {
    input: OrchestrationGetThreadDetailPageInput,
    output: OrchestrationThreadDetailSnapshot,
  },
} as const;

export class OrchestrationGetSnapshotError extends Schema.TaggedErrorClass<OrchestrationGetSnapshotError>()(
  "OrchestrationGetSnapshotError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {}

export class OrchestrationDispatchCommandError extends Schema.TaggedErrorClass<OrchestrationDispatchCommandError>()(
  "OrchestrationDispatchCommandError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {}

export class OrchestrationGetTurnDiffError extends Schema.TaggedErrorClass<OrchestrationGetTurnDiffError>()(
  "OrchestrationGetTurnDiffError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {}

export class OrchestrationGetFullThreadDiffError extends Schema.TaggedErrorClass<OrchestrationGetFullThreadDiffError>()(
  "OrchestrationGetFullThreadDiffError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {}

export class OrchestrationReplayEventsError extends Schema.TaggedErrorClass<OrchestrationReplayEventsError>()(
  "OrchestrationReplayEventsError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {}

export class OrchestrationSearchThreadsError extends Schema.TaggedErrorClass<OrchestrationSearchThreadsError>()(
  "OrchestrationSearchThreadsError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {}
